import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { finalize, Observable, timeout } from 'rxjs';
import { LoadingIndicatorService } from '#services';
import { HTTP_TIMEOUT_MS } from '../http-timeout.token';

/**
 * Global HTTP concern for every `/api` call:
 *  - bounds each request with a timeout so a stalled request (e.g. a hung MSAL
 *    token acquisition) can never hang the UI forever;
 *  - tracks the request in the LoadingIndicatorService and always releases it
 *    via `finalize`, so the global spinner can never get stuck "on".
 *
 * The timeout is per-request: callers can raise it for long-running endpoints
 * (e.g. Foundry agent chat) by setting HTTP_TIMEOUT_MS on the request context;
 * otherwise the default (DEFAULT_HTTP_TIMEOUT_MS) applies.
 *
 * Registered BEFORE the MsalInterceptor so its timeout wraps the token
 * acquisition too.
 */
@Injectable()
export class HttpLoadingInterceptor implements HttpInterceptor {
  private _loadingIndicator = inject(LoadingIndicatorService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Non-`/api` requests (e.g. i18n/asset loads) pass through untouched — no
    // timeout and no spinner tracking.
    if (!req.url.includes('/api')) {
      return next.handle(req);
    }

    this._loadingIndicator.increment();

    return next.handle(req).pipe(
      timeout(req.context.get(HTTP_TIMEOUT_MS)),
      finalize(() => this._loadingIndicator.decrement())
    );
  }
}
